import {
  type UsersListModel,
  type UsersListWithUploadResultsModel,
  UsersListsWithUploadResultsModel,
} from '@/features/mold/models/users-list';
import { t } from '@/i18n';
import {
  CSV_LINE_BREAK,
  datetimeFormat,
  downloadCsv,
  escapeForCSV,
} from '@/utils';
import type { z } from 'zod';

type UsersList = z.infer<typeof UsersListModel>;
type UsersListWithUploadResults = z.infer<
  typeof UsersListWithUploadResultsModel
>;

type FixedHeaderKey = 'results' | 'email' | 'name' | 'modifiedAt';
type OrganizationHeaderKey = 'organizationName' | 'organizationCode';
type HeaderKey = FixedHeaderKey | OrganizationHeaderKey;

const USER_LIST_CSV_HEADER_ORDER: FixedHeaderKey[] = [
  'email',
  'name',
  'modifiedAt',
];

const USER_LIST_UPLOAD_RESULT_CSV_HEADER_ORDER: FixedHeaderKey[] = [
  'results',
  'email',
  'name',
];

const ORGANIZATIONS_HEADER_ORDER: OrganizationHeaderKey[] = [
  'organizationName',
  'organizationCode',
];

export const USER_LIST_CSV_HEADER: { [k in HeaderKey]: string } = {
  results: t('user-list-csv-header-organization-test-result'),
  email: t('user-list-csv-header-email'),
  name: t('user-list-csv-header-name'),
  modifiedAt: t('user-list-csv-header-modified-at'),
  organizationName: t('user-list-csv-header-organization-name'),
  organizationCode: t('user-list-csv-header-organization-code'),
};

const createCsv = ({
  fixedHeaderKeys,
  data,
}: {
  fixedHeaderKeys: FixedHeaderKey[];
  data: UsersList | UsersListWithUploadResults;
}) => {
  const maxOrganizationLength = Math.max(
    ...data.map(({ organizations }) => organizations.length),
  );

  const headerKeys: HeaderKey[] = [
    ...fixedHeaderKeys,
    ...new Array(maxOrganizationLength)
      .fill(null)
      .map(() => ORGANIZATIONS_HEADER_ORDER),
  ].flat();

  const header = `${headerKeys
    .map((value) => {
      return `"${USER_LIST_CSV_HEADER[value].replace(/\n/g, '')}"`;
    })
    .join(',')}${CSV_LINE_BREAK}`;

  return data.reduce((str, report) => {
    const row = headerKeys
      .map((headerKey, i) => {
        if (
          headerKey === 'organizationCode' ||
          headerKey === 'organizationName'
        ) {
          const organizationIndex = Math.floor(
            (i - fixedHeaderKeys.length) / 2,
          );
          const organization = report.organizations[organizationIndex];
          const key =
            headerKey === 'organizationCode'
              ? 'flattenExternalSystemCode'
              : 'name';
          return escapeForCSV({ value: organization?.[key] ?? null });
        }

        if (headerKey === 'modifiedAt' && 'modifiedAt' in report) {
          const value = report[headerKey];
          return escapeForCSV({
            value: value ? datetimeFormat({ datetime: value }) : null,
          });
        }

        if (headerKey === 'results' && 'results' in report) {
          return escapeForCSV({ value: report.results.join(',') });
        }

        return escapeForCSV({ value: report[headerKey] });
      })
      .join(',');
    return `${str}${row}${CSV_LINE_BREAK}`;
  }, header);
};

const _getUserListCsvData = (params: {
  data: UsersList;
}) => createCsv({ ...params, fixedHeaderKeys: USER_LIST_CSV_HEADER_ORDER });

const getUserListUploadResultCsvData = (params: {
  data: UsersListWithUploadResults;
}) =>
  createCsv({
    ...params,
    fixedHeaderKeys: USER_LIST_UPLOAD_RESULT_CSV_HEADER_ORDER,
  });

const _downloadUsersUploadListCsv = (jsonData: any, fileName: string) => {
  const { users, allUsers } = UsersListsWithUploadResultsModel.parse(jsonData);
  const usersCsvData = getUserListUploadResultCsvData({
    data: users,
  });
  const allUsersData = getUserListUploadResultCsvData({
    data: allUsers,
  });
  downloadCsv(usersCsvData, fileName);
  downloadCsv(
    allUsersData,
    `${fileName}${t('bracket-full-width-start')}${t('all-users-list')}${t('bracket-full-width-end')}`,
  );
};
